/**
 * localStorage / sessionStorage that never throw.
 *
 * Browsers with blocked site data (Safari "Block all cookies", Chrome blocked
 * cookies) throw a SecurityError on `window.localStorage` itself, and Android
 * WebViews with DOM storage off (ad and in-app webviews) return `null`. A read
 * during render then unmounted the whole React root, leaving a blank page.
 *
 * Every call goes to the real storage when it works. When it does not (no
 * storage, or the call throws, e.g. a full quota), the value is kept in memory
 * for this page load instead, so the app behaves as with an empty storage.
 * `setItem()` returns whether the value was persisted, for callers that must
 * not rely on a value surviving a reload (the chunk reload guard).
 */
export interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): boolean;
  removeItem(key: string): void;
  /** Every key, persisted or in memory. */
  keys(): string[];
}

type StorageName = 'localStorage' | 'sessionStorage';

const createSafeStorage = (name: StorageName): SafeStorage => {
  const memory = new Map<string, string>();

  const real = (): Storage | null => {
    try {
      return typeof window !== 'undefined' ? window[name] ?? null : null;
    } catch {
      return null;
    }
  };

  return {
    getItem(key) {
      if (memory.has(key)) {
        return memory.get(key) ?? null;
      }
      try {
        return real()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        const storage = real();
        if (storage) {
          storage.setItem(key, value);
          memory.delete(key);
          return true;
        }
      } catch {
        // fall through to memory
      }
      memory.set(key, value);
      return false;
    },
    removeItem(key) {
      memory.delete(key);
      try {
        real()?.removeItem(key);
      } catch {
        // nothing stored there
      }
    },
    keys() {
      let stored: string[] = [];
      try {
        const storage = real();
        stored = storage ? Object.keys(storage) : [];
      } catch {
        stored = [];
      }
      return Array.from(new Set([...stored, ...memory.keys()]));
    },
  };
};

export const safeLocalStorage = createSafeStorage('localStorage');
export const safeSessionStorage = createSafeStorage('sessionStorage');
